import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { HeaderSearch } from '@/components/shell/HeaderSearch'
import { useAuth } from '@/features/auth/auth-context'
import { StreakPill } from '@/features/dashboard/StreakPill'
import { NotificationsBell } from '@/features/notifications/NotificationsBell'
import { t, useLanguage } from '@/lib/i18n'
import { AvatarMenu } from './AvatarMenu'
import { Icon } from './glyphs'
import { NavPill } from './NavPill'
import { R5Logo } from './R5Logo'
import { SampleChip } from './SampleChip'
import { activeNavIndex, primaryNavItems, type NavItem } from './nav-items'
import { COMPACT_QUERY, PHONE_QUERY, useMediaQuery } from './use-media-query'
import { useStretchIndicator } from './stretch-indicator'

/** The destinations as glyph-and-label pills, with one shared tint-navy pill behind them that stretches between them. */
export function PrimaryNav({ items = primaryNavItems() }: { items?: NavItem[] }) {
  const { pathname } = useLocation()
  const active = activeNavIndex(items, pathname)
  const navRef = useRef<HTMLElement>(null)
  const indicatorRef = useRef<HTMLSpanElement>(null)
  useStretchIndicator({ containerRef: navRef, indicatorRef, activeIndex: active, itemSelector: '.r5-nav__item', round: '999px' })
  return (
    <nav ref={navRef} className="r5-nav" aria-label={t('Primary navigation')}>
      <span ref={indicatorRef} className="r5-nav__indicator" aria-hidden="true" />
      {items.map((item, index) => (
        <NavPill key={item.to} to={item.to} label={item.label()} glyph={item.glyph} active={index === active} />
      ))}
    </nav>
  )
}

/**
 * The existing practice-test search. At 1100px and below it collapses to an icon button that opens the same field as an
 * overlay under the bar (Escape, an outside press or a route change closes it).
 */
function SearchSlot() {
  const compact = useMediaQuery(COMPACT_QUERY)
  const { demo } = useAuth()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => { setOpen(false) }, [pathname, compact])
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // A demo session has no search (HeaderSearch renders nothing there), so no empty icon button or bar either.
  if (demo) return null
  if (!compact) return <HeaderSearch />
  return (
    <div ref={rootRef} className="r5-searchslot">
      <button ref={buttonRef} type="button" className="r5-iconbtn r5-tap" aria-label={t('Search tests')} aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        <Icon name={open ? 'close' : 'search'} />
      </button>
      {open && <div className="r5-searchslot__overlay"><HeaderSearch /></div>}
    </div>
  )
}

/**
 * Phone: the page's own heading text, shown in the bar once that heading has scrolled under it. Home shows the logo instead.
 */
function PhoneTitle() {
  const { pathname } = useLocation()
  const [title, setTitle] = useState('')
  const [shown, setShown] = useState(false)
  useEffect(() => {
    setShown(false)
    let observer: IntersectionObserver | null = null
    const timer = window.setTimeout(() => {
      const heading = document.querySelector<HTMLElement>('#main h1')
      setTitle(heading?.textContent?.trim() ?? '')
      if (!heading || typeof IntersectionObserver === 'undefined') return
      observer = new IntersectionObserver(([entry]) => setShown(!entry!.isIntersecting && entry!.boundingClientRect.top < 80), { rootMargin: '-56px 0px 0px 0px' })
      observer.observe(heading)
    }, 60)
    return () => { window.clearTimeout(timer); observer?.disconnect() }
  }, [pathname])
  return <p className="r5-topbar__title" aria-hidden="true" data-shown={shown ? 'true' : 'false'}>{title}</p>
}

/**
 * The student top bar (APP-DESIGN-DIRECTION 4.1). Desktop and tablet: white, 64px, a hairline under it, sticky. Start to end:
 * the logo, the five destinations as glyph pills, then the sample-data chip (demo only), search, the streak pill,
 * notifications and the avatar. Phone (760px and below): a 56px app bar with the logo on Home or the page title elsewhere,
 * then streak, bell and avatar; the destinations move to the floating tab bar. `view-transition-name: r5-topbar` keeps it
 * still through a route change.
 */
export function TopBar() {
  useLanguage()
  const phone = useMediaQuery(PHONE_QUERY)
  const compact = useMediaQuery(COMPACT_QUERY)
  const { pathname } = useLocation()
  const home = pathname === '/home' || pathname === '/dashboard'
  return (
    <header className="r5-topbar">
      <div className="r5-topbar__in">
        {(!phone || home) && <R5Logo />}
        {/* Inner pages on a phone: the wordmark holds the slot until the page's own title scrolls away, then the title
            takes its place, so the top start of the bar is never empty. */}
        {phone && !home && <div className="r5-topbar__slot"><R5Logo /><PhoneTitle /></div>}
        {!phone && <PrimaryNav />}
        <div className="r5-topbar__spacer" />
        <div className="r5-topbar__end">
          <SampleChip />
          {!phone && <SearchSlot />}
          <StreakPill compact={compact} />
          <NotificationsBell />
          <AvatarMenu />
        </div>
      </div>
    </header>
  )
}
